import { fetchAPI } from '@quickbit/backoffice-core-frontend';
import { Merchant, MerchantLogo } from 'tabs/types';
import { KybSave, KybRead } from './types';

export interface MerchantSearchFilters {
  name?: string | null;
  merchantId?: string | null;
  userId?: string | null;
}

export interface MerchantMinimal {
  uuid: string;
  name: string;
  displayName: string;
  userId: string;
  countryCode: string;
  deletedAt?: Date;
  status: string;
  callbackUrl?: string;
}

export interface MerchantSearchResponse {
  merchants: MerchantMinimal[];
  aggregatedData: { count: number };
}

export interface Pagination {
  page: number;
  size: number;
}

export enum ContactStatus {
  ACTIVE = 'ACTIVE',
  PENDING_TRAVEL_RULE = 'PENDING_TRAVEL_RULE',
  PENDING_PROOF_OF_OWNERSHIP = 'PENDING_PROOF_OF_OWNERSHIP',
}

export interface Recipient {
  uuid: string;
  address: string;
  label: string;
  createdBy: string;
  createdByEmail: string | null;
  approvedBy: string | null;
  approvedByEmail: string | null;
  isInternal: boolean;
  currencyCode: string;
  status: ContactStatus;
}

export interface ProofOfOwnershipFile {
  url: string;
  name: string;
}

const queryKeys = {
  all: () => ['recipients'] as const,
  recipients: (merchantId?: string, currency?: string) =>
    [...queryKeys.all(), 'recipients', merchantId, currency] as const,
  proofOfOwnershipFiles: (merchantId?: string, recipientId?: string) =>
    [
      ...queryKeys.all(),
      'proof-of-ownership-files',
      merchantId,
      recipientId,
    ] as const,
};

export const searchMerchants = async ({
  filters,
  pagination,
}: {
  filters: MerchantSearchFilters;
  pagination: Pagination;
}) => {
  const response = await fetchAPI<MerchantSearchResponse>(
    'v1/merchants:search',
    {
      method: 'POST',
      body: {
        filters,
        pagination,
      },
    }
  );

  return {
    ...response,
    merchants: response.merchants.map((m) => ({
      ...m,
      deletedAt: m.deletedAt ? new Date(m.deletedAt) : undefined,
    })),
  };
};

export const addMerchant = (
  merchant: Merchant,
  kyb: KybSave,
  email: string
) => {
  const newKyb = {
    ...kyb,
    incorporationDate: kyb.incorporationDate
      ? formatDate(kyb.incorporationDate)
      : null,
    onboardingDate: formatDate(kyb.onboardingDate),
    relatedPeople: kyb.relatedPeople.map((p) => ({
      ...p,
      dateOfBirth: formatDate(p.dateOfBirth),
    })),
  };
  return fetchAPI<Merchant & { apiKey: string }>('v1/merchant', {
    method: 'POST',
    body: {
      merchant,
      kyb: newKyb,
      email,
    },
  });
};

export const updateMerchant = (merchant: Merchant) => {
  return fetchAPI<Merchant>(`v1/merchants/${merchant.uuid}`, {
    method: 'PUT',
    body: {
      ...merchant,
    },
  });
};

export const deleteMerchant = (merchantId: string) => {
  return fetchAPI(`v1/merchants/${merchantId}`, {
    method: 'DELETE',
  });
};

export const getKYB = async (merchantId: string): Promise<KybRead> => {
  const response = await fetchAPI<KybRead>(
    `v1/organizations/${merchantId}?include_changelog=true`
  );
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  response.changelog = response.changelog || [];
  return {
    ...response,
    incorporationDate: response.incorporationDate
      ? new Date(response.incorporationDate)
      : null,
    onboardingDate: new Date(response.onboardingDate),
    relatedPeople: response.relatedPeople.map((p) => ({
      ...p,
      dateOfBirth: new Date(p.dateOfBirth),
    })),
    changelog: response.changelog.map((cl) => ({
      ...cl,
      created: new Date(cl.created),
    })),
    settlementReportEmails: response.settlementReportEmails ?? null,
  };
};

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const saveKYB = (merchantId: string, kyb: KybSave): Promise<KybSave> => {
  const newKyb = {
    ...kyb,
    incorporationDate: kyb.incorporationDate
      ? formatDate(kyb.incorporationDate)
      : null,
    onboardingDate: formatDate(kyb.onboardingDate),
    relatedPeople: kyb.relatedPeople.map((p) => ({
      ...p,
      dateOfBirth: formatDate(p.dateOfBirth),
    })),
  };
  return fetchAPI(`v1/organizations/${merchantId}`, {
    method: 'POST',
    body: { ...newKyb },
  });
};

export const postMerchantLogo = async (
  merchantId: string,
  logoData: {
    encodedImage?: string;
    fileName?: string;
    enabled: boolean;
  }
) => {
  const data = await fetchAPI(`v1/merchant/${merchantId}/branding/logo`, {
    method: 'POST',
    body: {
      ...logoData,
    },
  });
  return data;
};

export const getMerchantLogo = async (
  merchantId: string
): Promise<MerchantLogo> => {
  return await fetchAPI(`v1/merchant/${merchantId}/branding/logo`, {
    method: 'GET',
  });
};
